import type { SlotType } from "@/types/entities/templates";
import { normalizeSlotName } from "./index";

export interface SlotMetadata {
  display_name: string;
  mapping_name: string;
  type: SlotType;
  configuration: {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
    heading?: number;
    required: boolean;
  };
}

/**
 * Extrae los slots del HTML con su metadata de estilos
 */
export const extractSlotsWithMetadata = (html: string): SlotMetadata[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const slotsMap = new Map<string, SlotMetadata>();

  const walker = document.createTreeWalker(
    doc.body,
    NodeFilter.SHOW_TEXT,
    null,
  );

  let currentNode: Node | null;
  while ((currentNode = walker.nextNode())) {
    const text = currentNode.textContent || "";
    const slotPattern = /\{\{([a-z0-9_-]+)(?::([a-z]+))?\}\}/gi;
    let match;

    while ((match = slotPattern.exec(text)) !== null) {
      const slotName = match[1];
      const slotType = (match[2] as SlotType) || "string";

      if (slotsMap.has(slotName)) continue;

      const parentElement = currentNode.parentElement;
      if (!parentElement) continue;

      const computedStyle = window.getComputedStyle(parentElement);
      const isBold =
        computedStyle.fontWeight === "700" ||
        computedStyle.fontWeight === "bold" ||
        parentElement.tagName === "STRONG" ||
        parentElement.tagName === "B";

      const isItalic =
        computedStyle.fontStyle === "italic" ||
        parentElement.tagName === "EM" ||
        parentElement.tagName === "I";

      const textDecoration = computedStyle.textDecoration;
      const isUnderline =
        textDecoration.includes("underline") || parentElement.tagName === "U";

      const isStrike =
        textDecoration.includes("line-through") ||
        parentElement.tagName === "S" ||
        parentElement.tagName === "STRIKE";

      slotsMap.set(slotName, {
        display_name: slotName,
        mapping_name: normalizeSlotName(slotName),
        type: slotType.toUpperCase() as SlotType,
        configuration: {
          bold: isBold,
          italic: isItalic,
          underline: isUnderline,
          strike: isStrike,
          required: true,
        },
      });
    }
  }

  return Array.from(slotsMap.values());
};

/**
 * Convierte slots del formato del editor al formato del backend
 * {{nombre}} → $text_nombre
 * {{edad:number}} → $float_edad
 * {{fecha:date}} → $date_fecha
 */
export const transformSlotsToBackendFormat = (html: string): string => {
  const slotPattern = /\{\{([a-z0-9_-]+)(?::([a-z]+))?\}\}\s*/gi;

  return html.replace(slotPattern, (_match, name, type) => {
    const slotType = type || "string";

    let backendType: string;
    switch (slotType.toLowerCase()) {
      case "number":
        backendType = "float";
        break;
      case "date":
        backendType = "date";
        break;
      case "string":
      default:
        backendType = "text";
        break;
    }

    return `$${backendType}_${name} `;
  });
};

/**
 * Convierte slots del formato del backend al formato del editor
 * $text_nombre → {{nombre}}
 * $float_edad → {{edad:number}}
 * $date_fecha → {{fecha:date}}
 */
export const transformSlotsFromBackendFormat = (html: string): string => {
  const backendPattern = /\$(text|float|date)_([a-z0-9_-]+)\s*/gi;

  const result = html.replace(backendPattern, (_match, type, name) => {
    let editorType: string;
    let replacement: string;

    switch (type.toLowerCase()) {
      case "float":
        editorType = "number";
        replacement = `{{${name}:${editorType}}} \u200B`;
        break;
      case "date":
        editorType = "date";
        replacement = `{{${name}:${editorType}}} \u200B`;
        break;
      case "text":
      default:
        replacement = `{{${name}}} \u200B`;
        break;
    }

    return replacement;
  });

  return result;
};

/**
 * Extrae un Map simple de slots (nombre → tipo)
 */
export const extractSlotsMap = (html: string): Map<string, SlotType> => {
  const slotPattern = /\{\{([a-z0-9_-]+)(?::([a-z]+))?\}\}/gi;
  const slots = new Map<string, SlotType>();
  let match;

  while ((match = slotPattern.exec(html)) !== null) {
    const name = match[1];
    const type = (match[2] as SlotType) || "string";
    slots.set(name, type);
  }

  return slots;
};

/**
 * Agrega espacios después de los slots para que se rendericen como widgets
 * Debe ejecutarse después de que el editor cargue el contenido
 */
export const fixSlotSpaces = (html: string): string => {
  return html.replace(/(\{\{[a-z0-9_-]+(?::[a-z]+)?\}\})(?! )/gi, "$1 ");
};

// Función para normalizar HTML
export const normalizeHTML = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Obtener solo el texto plano para comparar
  const textContent = doc.body.textContent || "";

  // Remover todos los espacios en blanco múltiples y trim
  return textContent.replace(/\s+/g, " ").trim();
};
